import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { getLocalizedString } from "@icarius-localization/strings";
import { CloseIcon } from "@icarius-icons";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
} from "@material-ui/core";
import { formatDraftToText } from "@icarius-utils/format";
import DialogTitleDivider from "@icarius-common/dialogTitleDivider";
import ButtonDialogAction from "@icarius-common/buttonDialogAction";
import PaperDraggable from "@icarius-common/paperDraggable";
import DialogTransition from "@icarius-common/dialogTransition";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import TextEditor from "@icarius-common/textEditor";
import { gradeActivityFromSpecificTrainingAction } from "@icarius-pages/myTraining/actions";
import Container from "../../../../../container";
import GradesHistoryContent from "../gradesHistoryContent";
import ExerciseResolution from "./exerciseResolution";
import EvaluationResolution from "./evaluationResolution";
import StatusChip from "../../../mainContent/statusChip";
import CustomNumberFormat from '@icarius-common/dynamicInputs/customNumberFormat';

const calculateScore = (questions, descriptiveCorrection, maxScore) => {
  const amountOfOptionQuestionsCorrect = questions.filter((question) => question.isCorrect).length;
  const amountOfDescriptiveQuestionsCorrect = Object.values(descriptiveCorrection).filter((answer) => Boolean(answer)).length;
  return Math.ceil((amountOfOptionQuestionsCorrect + amountOfDescriptiveQuestionsCorrect) / questions.length * maxScore);
}

const GradeActivityDialog = (props) => {

  const {
    open,
    readOnly,
    trainingData,
    activityData,
    resolution,
    handleClose,
  } = props;

  const isGradeOverriding = resolution?.currentAttempt >= resolution?.attempts;

  const dispatch = useDispatch();
  const [feedback, setFeedback] = useState("");
  const [feedbackHTML, setFeedbackHTML] = useState("");
  const [descriptiveCorrection, setDescriptiveCorrection] = useState(() => {
    if (!resolution?.questions?.length) return null;

    const newDescriptiveCorrection = {};

    resolution.questions.forEach((question, questionIndex) => {
      if (question.type === "D") {
        newDescriptiveCorrection[questionIndex] = Boolean(question.isCorrect);
      }
    });

    return newDescriptiveCorrection;
  });
  const [score, setScore] = useState(() => {
    if (resolution?.score) return resolution.score;
    if (!resolution?.questions?.length) return "";
    return calculateScore(resolution.questions, {}, resolution.rule.max);
  });

  const handleSetDescriptiveCorrectionValue = (questionIndex) => {
    const newDescriptiveCorrection = { ...descriptiveCorrection };
    newDescriptiveCorrection[questionIndex] = !Boolean(newDescriptiveCorrection[questionIndex]);
    setDescriptiveCorrection(newDescriptiveCorrection);
    setScore(calculateScore(resolution.questions, newDescriptiveCorrection, resolution.rule.max));
  }

  const submit = () => {
    if ((isGradeOverriding && !formatDraftToText(feedback).trim().length) || (!score && score !== 0) || score < resolution.rule.min || score > resolution.rule.max) {
      dispatch(openSnackbarAction({ msg: getLocalizedString("invalidData"), severity: "error", duration: 10000 }));
      return;
    }

    dispatch(gradeActivityFromSpecificTrainingAction(trainingData.id, activityData.id, resolution.code, { score, feedback, feedbackHTML, descriptiveCorrection }))
      .then((resp) => {
        if (resp.status === 200) {
          handleClose();
        }
      })
  }

  return (
    <Dialog
      open={open}
      TransitionComponent={DialogTransition}
      PaperComponent={PaperDraggable}
      maxWidth={"md"}
      fullWidth={true}
    >
      <div className={"dialog-container"}>
        <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
          {activityData.name}
          <DialogTitleDivider />
        </DialogTitle>
        <CloseIcon className={"dialog-close-icon icon"} onClick={handleClose} />
        <DialogContent style={{ paddingTop: 0 }}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 15,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                flexDirection: "column",
              }}
            >
              <Typography align="center" style={{ fontSize: 20, fontWeight: 600 }}>
                {resolution.name}
              </Typography>
              <Typography align="center">
                {`${getLocalizedString("attempt")} ${resolution.currentAttempt} ${getLocalizedString("of")} ${resolution.attempts}`}
              </Typography>
            </div>
            <StatusChip
              passed={resolution.passed}
              showIcon
            />
            <ExerciseResolution
              resolution={resolution}
            />
            <EvaluationResolution
              readOnly={readOnly}
              resolution={resolution}
              descriptiveCorrection={descriptiveCorrection}
              handleSetDescriptiveCorrectionValue={handleSetDescriptiveCorrectionValue}
            />
            {
              Boolean(resolution.grades?.length) &&
              <GradesHistoryContent
                data={resolution.grades}
              />
            }
            {
              !readOnly &&
              <Container
                title={`${getLocalizedString("grading")}:`}
                style={{ width: "100%" }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 20,
                  }}
                >
                  <TextField
                    required
                    fullWidth
                    margin={"none"}
                    label={getLocalizedString("score")}
                    InputProps={{ inputComponent: CustomNumberFormat }}
                    value={score}
                    onChange={(e) => setScore(e.target.value)}
                    inputProps={{ min: resolution.rule.min, max: resolution.rule.max, maxLength: 3 , isAllowed: (v) => v.floatValue === undefined || v.floatValue <= resolution.rule.max}}
                    helperText={getLocalizedString("minMaxPassValues", { min: resolution.rule.min, max: resolution.rule.max, pass: resolution.rule.pass })}
                  />
                  <div style={{ width: "100%" }}>
                    <Typography variant="caption" className="whiteText" style={{ marginBottom: 5 }}>
                      {getLocalizedString(isGradeOverriding ? "enterRequiredFeedback" : "enterOptionalFeedback")}
                      {
                        isGradeOverriding &&
                        <span style={{ fontWeight: 400, marginLeft: 2 }} className="errorColor" variant="caption">
                          {"*"}
                        </span>
                      }
                    </Typography>
                    <TextEditor
                      allowImages
                      initialValue={feedback}
                      handleChange={(value, htmlValue) => {
                        setFeedback(value);
                        setFeedbackHTML(htmlValue);
                      }}
                    />
                  </div>
                </div>
              </Container>
            }
          </div>
        </DialogContent>
        <DialogActions>
          {
            !readOnly &&
            <>
              <ButtonDialogAction onClick={handleClose} text={getLocalizedString("cancel")} />
              <ButtonDialogAction onClick={submit} isAccept text={getLocalizedString("accept")} />
            </>
          }
          {
            readOnly &&
            <ButtonDialogAction onClick={handleClose} isAccept text={getLocalizedString("close")} />
          }
        </DialogActions>
      </div>
    </Dialog>
  );
}

export default GradeActivityDialog;